// pages/PriceTagDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import priceTagService from '../services/priceTagService';
import { getIcon } from '../utils/icons';

/**
 * Страница просмотра информации о ценнике
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const PriceTagDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [priceTag, setPriceTag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    fetchPriceTag();
  }, [id]);

  /**
   * Загрузка данных о ценнике с сервера
   */
  const fetchPriceTag = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await priceTagService.getPriceTagById(id);
      setPriceTag(data);
      
      // Получаем presigned URL для изображения, если оно есть
      if (data.image && data.image.id) {
        try {
          const url = await priceTagService.getImagePresignedUrl(data.image.id);
          setImageUrl(url);
        } catch (err) {
          console.error('Ошибка получения presigned URL:', err);
          // Fallback на filePath
          setImageUrl(data.image.filePath);
        }
      }
    } catch (err) {
      console.error('Ошибка при загрузке ценника:', err);
      setError('Не удалось загрузить данные ценника. Пожалуйста, попробуйте позже.');
      toast.error('Ошибка при загрузке ценника');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Обработчик верификации ценника
   */
  const handleVerify = async () => {
    try {
      if (window.confirm('Вы уверены, что хотите верифицировать этот ценник?')) {
        await priceTagService.verifyPriceTag(id);
        toast.success('Ценник успешно верифицирован');
        fetchPriceTag(); // Перезагружаем данные
      }
    } catch (err) {
      console.error('Ошибка при верификации ценника:', err);
      toast.error('Не удалось верифицировать ценник');
    }
  };

  /**
   * Получение отображаемого типа ценника
   * @param {string} tagType - Тип ценника
   * @returns {string} - Отображаемый тип
   */
  const getTagTypeDisplay = (tagType) => {
    const types = {
      'Printed': 'Печатный',
      'Handwritten': 'Рукописный',
      'Electronic': 'Электронный'
    };
    return types[tagType] || tagType;
  };

  /**
   * Форматирование даты
   * @param {Date} date - Дата
   * @returns {string} - Форматированная дата
   */
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('ru-RU');
  };

  /**
   * Перейти к редактированию ценника
   */
  const handleEdit = () => {
    navigate(`/price-tags/${id}/edit`);
  };

  /**
   * Вернуться к списку ценников
   */
  const handleBack = () => {
    const savedUrl = sessionStorage.getItem('priceTagsListUrl');
    navigate(savedUrl || '/price-tags');
  };

  /**
   * Открыть модальное окно с изображением
   */
  const handleOpenImageModal = () => {
    setImageModalOpen(true);
  };

  /**
   * Закрыть модальное окно с изображением
   */
  const handleCloseImageModal = () => {
    setImageModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-80">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-xl font-bold text-red-700 mb-2">Ошибка</h2>
        <p className="text-red-600">{error}</p>
        <button
          onClick={handleBack}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
        >
          Вернуться к списку
        </button>
      </div>
    );
  }

  if (!priceTag) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h2 className="text-xl font-bold text-yellow-700 mb-2">Ценник не найден</h2>
        <p className="text-yellow-600">
          Запрашиваемый ценник с ID {id} не найден в системе или у вас нет прав доступа к нему.
        </p>
        <button
          onClick={handleBack}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
        >
          Вернуться к списку
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-4 text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Информация о ценнике</h1>
        </div>
        <div className="flex space-x-2">
          {!priceTag.isVerified && (
            <button
              onClick={handleVerify}
              className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Верифицировать
            </button>
          )}
          <button
            onClick={handleEdit}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            {getIcon('edit')}
            <span className="ml-2">Редактировать</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
          {/* Левая колонка - фото ценника */}
          <div className="md:col-span-1">
            <div className="bg-gray-100 rounded-lg p-2 h-full flex flex-col">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Изображение ценника</h3>
              {priceTag.image ? (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <div className="relative aspect-square w-full cursor-pointer" onClick={handleOpenImageModal}>
                    <img 
                      src={imageUrl || priceTag.image.filePath} 
                      alt="Ценник" 
                      className="object-contain rounded-md w-full h-full"
                    />
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className="text-sm text-gray-500">Нажмите на изображение для увеличения</span>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-200 text-gray-400 rounded-md">
                  <p>Изображение отсутствует</p>
                </div>
              )}
            </div>
          </div>

          {/* Правая колонка - информация о ценнике */}
          <div className="md:col-span-2">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Детальная информация</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-500">Товар</div>
                    <div className="text-base font-semibold text-gray-900">
                      {priceTag.product ? (
                        <Link 
                          to={`/products/${priceTag.product.id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {priceTag.product.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">Не указан</span>
                      )}
                    </div>
                    {priceTag.unifiedPriceText && (
                      <div className="text-sm text-gray-600 mt-1">
                        {priceTag.unifiedPriceText}
                      </div>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-500">Магазин</div>
                    <div className="text-base font-semibold text-gray-900">
                      {priceTag.store ? (
                        <Link 
                          to={`/stores/${priceTag.store.id}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {priceTag.store.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">Не указан</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-500">Цена</div>
                    <div className="text-xl font-bold text-gray-900">{priceTag.price?.toFixed(2)} ₽</div>
                    {priceTag.pricePerUnit && (
                      <div className="text-sm text-gray-500">
                        {priceTag.pricePerUnit.toFixed(2)} ₽/{priceTag.unit?.shortName || 'ед.'}
                      </div>
                    )}
                  </div>
                  
                  {priceTag.discountPrice && (
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-500">Цена со скидкой</div>
                      <div className="text-xl font-bold text-red-600">{priceTag.discountPrice.toFixed(2)} ₽</div>
                      <div className="text-sm text-gray-500">
                        Скидка: {((1 - priceTag.discountPrice / priceTag.price) * 100).toFixed(0)}%
                      </div>
                    </div>
                  )}
                  
                  {/* Граммовка из базы */}
                  {(priceTag.originalWeight || priceTag.overrideWeight || priceTag.overrideVolume) && (
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-500">
                        {priceTag.originalWeight || priceTag.overrideWeight ? 'Вес' : 'Объем'}
                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {priceTag.originalWeight 
                          ? `${priceTag.originalWeight} ${priceTag.originalUnit || ''}`
                          : priceTag.overrideWeight 
                            ? `${priceTag.overrideWeight} ${priceTag.overrideWeightUnit === 'Kilogram' ? 'кг' : priceTag.overrideWeightUnit === 'Gram' ? 'г' : ''}`
                            : priceTag.overrideVolume
                              ? `${priceTag.overrideVolume} ${priceTag.overrideVolumeUnit === 'Liter' ? 'л' : priceTag.overrideVolumeUnit === 'Milliliter' ? 'мл' : ''}`
                              : ''}
                      </div>
                    </div>
                  )}
                  
                  {/* Пересчитанная цена на граммовку товара */}
                  {priceTag.recalculatedPriceForProductWeight && priceTag.product && priceTag.product.weight && priceTag.product.weightUnit && (
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-500">
                        Цена за {priceTag.product.weight} {priceTag.product.weightUnit === 'Kilogram' ? 'кг' : priceTag.product.weightUnit === 'Gram' ? 'г' : ''}
                      </div>
                      <div className="text-xl font-bold text-blue-600">
                        {priceTag.recalculatedPriceForProductWeight.toFixed(2)} ₽
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-500">Тип ценника</div>
                    <div className="text-base font-semibold text-gray-900">
                      {getTagTypeDisplay(priceTag.tagType)}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-500">Статус</div>
                    <div className="mt-1">
                      <span 
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          priceTag.isVerified 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {priceTag.isVerified ? 'Верифицирован' : 'Не верифицирован'}
                      </span>
                    </div>
                  </div>
                  
                  {priceTag.isVerified && priceTag.verifiedBy && (
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-500">Верифицирован</div>
                      <div className="text-base text-gray-900">
                        {priceTag.verifiedBy.fullName || 'Неизвестный пользователь'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(priceTag.verifiedAt)}
                      </div>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-500">Создан</div>
                    <div className="text-base text-gray-900">
                      {priceTag.createdBy ? priceTag.createdBy.fullName : 'Неизвестный пользователь'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDate(priceTag.createdAt)}
                    </div>
                  </div>
                  
                  {priceTag.currency && priceTag.currency !== 'RUB' && (
                    <div className="mb-4">
                      <div className="text-sm font-medium text-gray-500">Валюта</div>
                      <div className="text-base font-semibold text-gray-900">
                        {priceTag.currency}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Дополнительная информация о скидке */}
              {priceTag.discountStartDate && priceTag.discountEndDate && (
                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-medium text-yellow-800 mb-2">Информация о скидке</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">Начало скидки</div>
                      <div className="text-sm text-gray-900">{formatDate(priceTag.discountStartDate)}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Окончание скидки</div>
                      <div className="text-sm text-gray-900">{formatDate(priceTag.discountEndDate)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Информация о распознавании */}
              {priceTag.recognitionConfidence && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2">Информация о распознавании</h4>
                  <div>
                    <div className="text-sm font-medium text-gray-500">Уверенность распознавания</div>
                    <div className="flex items-center mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            priceTag.recognitionConfidence > 0.8 
                              ? 'bg-green-500' 
                              : priceTag.recognitionConfidence > 0.5 
                                ? 'bg-yellow-500' 
                                : 'bg-red-500'
                          }`} 
                          style={{ width: `${priceTag.recognitionConfidence * 100}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-700">
                        {(priceTag.recognitionConfidence * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно для просмотра изображения */}
      {imageModalOpen && priceTag.image && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black bg-opacity-75" 
            onClick={handleCloseImageModal}
          ></div>
          <div className="relative max-w-4xl max-h-full p-4">
            <div className="absolute top-4 right-4 z-10">
              <button 
                onClick={handleCloseImageModal} 
                className="bg-white bg-opacity-25 text-white p-2 rounded-full hover:bg-opacity-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img 
              src={imageUrl || priceTag.image.filePath} 
              alt="Ценник" 
              className="max-h-full max-w-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceTagDetailPage;